/**
 * Hook library vocabulary — the tag, status, and channel-fit values shared by
 * the `nova_hooks` writers (`save_hooks`, the Mini Sprint materializer), the
 * `list_hooks` reader, and the skill instructions that tell a model which
 * value to pick.
 *
 * Two independent axes describe a saved hook, and they are not
 * interchangeable:
 *
 *  - `angle` (already in the schema) — WHICH SEAM of the ICP the hook works:
 *    an entry point, a trigger moment, a psychographic dimension. It answers
 *    "what is this hook about".
 *  - `tag` (this module) — WHAT MECHANISM the line uses to pull the reader in.
 *    It answers "how does this hook work". Two hooks about the same trigger
 *    moment can use completely different mechanisms.
 *
 * Keeping both is what makes the library searchable in the way a coach
 * actually needs: "another hook for this trigger moment, but not another
 * question".
 *
 * Aliases: hook tags, hook taxonomy, hook mechanism, hook status, hook
 * channel fit, hook library vocabulary
 */

/**
 * The mechanism a hook uses. Single-valued: pick the one that carries most of
 * the pull, not every one that could apply.
 */
export const HOOK_TAGS = [
  "frustration",
  "readiness",
  "curiosity_gap",
  "mirror",
  "identity_gap",
  "speed_binary",
  "question_form",
] as const;

export type HookTag = (typeof HOOK_TAGS)[number];

/**
 * What each tag means, in the words a model needs to classify a line it just
 * wrote. Used verbatim in tool descriptions and skill instructions so the
 * vocabulary can never drift between where a hook is written and where it is
 * read back.
 *
 * `question_form` is deliberately last and deliberately the residual: a hook
 * shaped as a question usually also does one of the other six, and the more
 * specific tag wins. Reach for `question_form` only when the pull comes from
 * the question itself and nothing more specific fits.
 */
export const HOOK_TAG_DESCRIPTIONS: Record<HookTag, string> = {
  frustration:
    'Names a frustration the reader already feels, usually against something they are doing right — "still X even though you Y".',
  readiness:
    "Asks whether the reader is at the point of acting, and lets them answer it themselves.",
  curiosity_gap:
    "Points at a missing piece the reader cannot guess from the line, so the only way to close it is to keep reading.",
  mirror:
    "Holds up the reader's own professional standard and lets the gap between it and their situation do the work.",
  identity_gap:
    "Contrasts something the reader is demonstrably good at with something adjacent that stays hard for them.",
  speed_binary:
    "Sets a guessing state against a knowing state, so the cost of staying in the first one is obvious.",
  question_form:
    "The pull is the question itself. Use only when none of the six above fits — most questions are really a readiness or mirror hook.",
};

export function isHookTag(value: string): value is HookTag {
  return (HOOK_TAGS as readonly string[]).includes(value);
}

/** How many distinct mechanism tags a saved bank of hooks must span. */
export const MIN_DISTINCT_HOOK_TAGS = 3;

/**
 * Verify a bank of hooks pulls in at least `MIN_DISTINCT_HOOK_TAGS` different
 * ways. Ten hooks on one mechanism are one hook ten times, and a library like
 * that cannot answer "another hook, but not another question". Shared by both
 * hook writers — `save_hooks` and the Mini Sprint schema — so the rule and its
 * wording live in one place. Duplicate tags count once.
 */
export function checkDistinctHookTags(
  tags: readonly HookTag[],
): { ok: true } | { ok: false; message: string } {
  const used = new Set(tags);
  if (used.size >= MIN_DISTINCT_HOOK_TAGS) return { ok: true };

  const listed = HOOK_TAGS.filter((tag) => used.has(tag)).join(", ");
  return {
    ok: false,
    message: `The ${tags.length} hooks must use at least ${MIN_DISTINCT_HOOK_TAGS} distinct mechanism tags. You sent ${used.size} (${listed}). Ten hooks that all pull the same way is one hook ten times — rewrite some of them to work a different mechanism and try again.`,
  };
}

/**
 * Where a hook is in its life.
 *
 * `save_hooks` writes `approved`, because it only runs after the coach has
 * reacted to the shortlist and approved it. `draft` exists for hooks written
 * into the library without that moment, and `retired` for lines a coach has
 * stopped using — neither has a write path yet.
 */
export const HOOK_STATUSES = ["draft", "approved", "retired"] as const;

export type HookStatus = (typeof HOOK_STATUSES)[number];

/** What a freshly saved, coach-approved hook gets. */
export const DEFAULT_HOOK_STATUS: HookStatus = "approved";

/**
 * Channels a hook reads well in. Multi-valued: a line can work in a post and
 * a DM and not in an email. Matches the three guides in
 * `voice_profile.channels`, so a skill writing for one channel can filter the
 * library by the same key it reads its voice guide with.
 */
export const HOOK_CHANNELS = ["social", "email", "dm"] as const;

export type HookChannel = (typeof HOOK_CHANNELS)[number];

/**
 * The surface a hook promises for. `core_offer` promises the transformation,
 * `mini_sprint` promises the first win, `content` promises neither — it earns
 * attention for a post or article that sells nothing directly.
 */
export const HOOK_SURFACES = ["core_offer", "mini_sprint", "content"] as const;

export type HookSurface = (typeof HOOK_SURFACES)[number];
